import { Fragment, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { Check2, ChevronDown } from 'react-bootstrap-icons';
import {
  ALL_WINDOW_LABEL,
  discoverDataMonths,
  isoDay,
  setAllData,
  setCustomRange,
  setLastNDays,
  setMonth,
} from './shared';
import type { WindowState } from './shared';
import type { FleetAnalyticsSnapshot } from '@/types/fleetAnalytics';
import './WindowSelector.css';

interface WindowSelectorProps {
  data: FleetAnalyticsSnapshot;
  value: WindowState;
  onChange: (next: WindowState) => void;
}

const DAY_PRESETS = [7, 14, 30, 60, 90];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Which control the window lights up. */
type Active = { kind: 'days'; n: number } | { kind: 'all' } | { kind: 'month'; ym: string } | { kind: 'custom' };

/** A `YYYY-MM-DD` key as a count of days, read as the calendar day it names. */
function dayNumber(iso: string): number | null {
  const parts = ISO_DAY.exec(iso);
  if (!parts) return null;
  return Math.round(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) / DAY_MS);
}

/** "14 Sep" or "14 Sep 2026". The day is the one the key names, in no time zone. */
function formatDay(iso: string, withYear: boolean): string {
  const parts = ISO_DAY.exec(iso);
  const month = parts ? MONTHS_SHORT[Number(parts[2]) - 1] : undefined;
  if (!parts || !month) return iso;
  const day = `${Number(parts[3])} ${month}`;
  return withYear ? `${day} ${parts[1]}` : day;
}

/** "14 Sep to 13 Oct 2026". The first year is written only when the two differ. */
function formatRange(start: string, end: string): string {
  if (start === end) return formatDay(end, true);
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  return `${formatDay(start, !sameYear)} to ${formatDay(end, true)}`;
}

/** "Sep 2026" for the button, "September" or "September 2026" for the list. */
function formatMonth(ym: string, names: string[], withYear: boolean): string {
  const parts = ISO_MONTH.exec(ym);
  const month = parts ? names[Number(parts[2]) - 1] : undefined;
  if (!parts || !month) return ym;
  return withYear ? `${month} ${parts[1]}` : month;
}

/** The month a range covers, when it runs from the first day of one month to its last. */
function wholeMonth(start: string, end: string): string | null {
  const from = ISO_DAY.exec(start);
  const to = ISO_DAY.exec(end);
  if (!from || !to || from[1] !== to[1] || from[2] !== to[2] || from[3] !== '01') return null;
  const lastDay = new Date(Date.UTC(Number(from[1]), Number(from[2]), 0)).getUTCDate();
  return Number(to[3]) === lastDay ? `${from[1]}-${from[2]}` : null;
}

/**
 * Which control is lit. The label a preset writes is read first, because on
 * the last day of a 30-day month "30d" and that month are the same dates.
 * After that the dates decide, so a custom range that is exactly a month
 * lights the month.
 */
function resolveActive(value: WindowState, today: string): Active {
  if (value.label === ALL_WINDOW_LABEL) return { kind: 'all' };
  const named = /^Last (\d+) days$/.exec(value.label);
  if (named && DAY_PRESETS.includes(Number(named[1]))) return { kind: 'days', n: Number(named[1]) };
  const month = wholeMonth(value.startDate, value.endDate);
  if (month) return { kind: 'month', ym: month };
  const start = dayNumber(value.startDate);
  const end = dayNumber(value.endDate);
  if (value.endDate === today && start !== null && end !== null && DAY_PRESETS.includes(end - start + 1)) {
    return { kind: 'days', n: end - start + 1 };
  }
  return { kind: 'custom' };
}

/**
 * The time window, in one row: the rolling presets, a month, or a custom
 * range. The line on the right always says which days the page's numbers cover.
 */
export function WindowSelector({ data, value, onChange }: WindowSelectorProps) {
  const today = isoDay(new Date());
  // Both are read from the data on every draw, so they follow the selection the page shows now.
  const everything = setAllData(data);
  const months = discoverDataMonths(data)
    .filter((ym) => ISO_MONTH.test(ym))
    .sort()
    .reverse();
  const years: Array<{ year: string; months: string[] }> = [];
  for (const ym of months) {
    const year = ym.slice(0, 4);
    const last = years[years.length - 1];
    if (last && last.year === year) last.months.push(ym);
    else years.push({ year, months: [ym] });
  }

  const active = resolveActive(value, today);

  // A window that runs past today, a month in progress, holds data only up to today.
  const shownEnd = value.endDate > today ? today : value.endDate;
  const firstDay = dayNumber(value.startDate);
  const lastDay = dayNumber(shownEnd);
  const days = firstDay !== null && lastDay !== null && lastDay >= firstDay ? lastDay - firstDay + 1 : null;

  // The custom dates are a draft until Apply. They restart from the window
  // whenever the window changes, so they never show a range that is not on screen.
  const windowKey = `${value.startDate}|${shownEnd}`;
  const [draft, setDraft] = useState({ key: windowKey, from: value.startDate, to: shownEnd });
  if (draft.key !== windowKey) setDraft({ key: windowKey, from: value.startDate, to: shownEnd });

  const [customOpen, setCustomOpen] = useState(false);
  const fromRef = useRef<HTMLInputElement>(null);
  const customToggleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (customOpen) fromRef.current?.focus();
  }, [customOpen]);

  // "All" means everything the selection on screen holds. When the selection
  // changes, or more of its history loads, the window moves with it. The ref
  // stops the same range from being sent twice in a row.
  const allActive = active.kind === 'all';
  const allStart = everything?.startDate;
  const allEnd = everything?.endDate;
  const sentAll = useRef<string | null>(null);
  useEffect(() => {
    if (!allActive || !allStart || !allEnd || (allStart === value.startDate && allEnd === value.endDate)) {
      sentAll.current = null;
      return;
    }
    const range = `${allStart}|${allEnd}`;
    if (sentAll.current === range) return;
    sentAll.current = range;
    onChange({ startDate: allStart, endDate: allEnd, label: ALL_WINDOW_LABEL });
  }, [allActive, allStart, allEnd, value.startDate, value.endDate, onChange]);

  const pickMonth = (ym: string) => {
    const [year, month] = ym.split('-').map(Number);
    onChange(setMonth(year, month - 1));
  };

  const toggleCustom = (open: boolean) => {
    // The popover opens on the window on screen, not on dates left from last time.
    if (open) setDraft({ key: windowKey, from: value.startDate, to: shownEnd });
    setCustomOpen(open);
  };

  const applyCustom = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.from || !draft.to) return;
    onChange(setCustomRange(draft.from, draft.to));
    setCustomOpen(false);
    customToggleRef.current?.focus();
  };

  const segment = (on: boolean) => (on ? 'window-selector__seg is-active' : 'window-selector__seg');

  return (
    <div className="window-selector">
      <div className="window-selector__track" role="group" aria-label="Time window">
        {DAY_PRESETS.map((n) => {
          const on = active.kind === 'days' && active.n === n;
          return (
            <button
              key={n}
              type="button"
              className={segment(on)}
              aria-pressed={on}
              aria-label={`Last ${n} days`}
              title={`Last ${n} days`}
              onClick={() => onChange(setLastNDays(n))}
            >
              {n}d
            </button>
          );
        })}
        <button
          type="button"
          className={segment(allActive)}
          aria-pressed={allActive}
          disabled={!everything}
          title={
            everything
              ? `Everything on record, from ${formatDay(everything.startDate, true)} to today`
              : 'There is no daily data for this selection.'
          }
          onClick={() => everything && onChange(everything)}
        >
          All
        </button>

        <span className="window-selector__divider" aria-hidden="true" />

        <Dropdown className="window-selector__drop">
          <Dropdown.Toggle
            as="button"
            type="button"
            bsPrefix="window-selector__seg"
            className={active.kind === 'month' ? 'is-active' : undefined}
            disabled={months.length === 0}
            title={months.length === 0 ? 'There is no month of data for this selection.' : 'Pick one month'}
          >
            {active.kind === 'month' ? formatMonth(active.ym, MONTHS_SHORT, true) : 'Month'}
            <ChevronDown className="window-selector__caret" aria-hidden="true" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="window-selector__menu">
            {years.map((group) => (
              <Fragment key={group.year}>
                {years.length > 1 && <Dropdown.Header>{group.year}</Dropdown.Header>}
                {group.months.map((ym) => {
                  const on = active.kind === 'month' && active.ym === ym;
                  return (
                    <Dropdown.Item
                      key={ym}
                      as="button"
                      type="button"
                      className={on ? 'window-selector__month is-current' : 'window-selector__month'}
                      aria-current={on ? 'true' : undefined}
                      onClick={() => pickMonth(ym)}
                    >
                      <span>{formatMonth(ym, MONTHS_LONG, years.length === 1)}</span>{' '}
                      {ym === today.slice(0, 7) && <span className="window-selector__month-note">so far</span>}
                      {on && <Check2 className="window-selector__month-check" aria-hidden="true" />}
                    </Dropdown.Item>
                  );
                })}
              </Fragment>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="window-selector__drop" show={customOpen} onToggle={toggleCustom} autoClose="outside">
          <Dropdown.Toggle
            ref={customToggleRef}
            as="button"
            type="button"
            bsPrefix="window-selector__seg"
            className={active.kind === 'custom' ? 'is-active' : undefined}
            title="Pick the first and the last day"
          >
            Custom
            <ChevronDown className="window-selector__caret" aria-hidden="true" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="window-selector__menu window-selector__menu--custom">
            <form className="window-selector__custom" onSubmit={applyCustom}>
              <label className="window-selector__field">
                <span className="window-selector__field-label">From</span>
                <Form.Control
                  ref={fromRef}
                  type="date"
                  size="sm"
                  value={draft.from}
                  max={today}
                  onChange={(event) => setDraft({ ...draft, from: event.target.value })}
                />
              </label>
              <label className="window-selector__field">
                <span className="window-selector__field-label">To</span>
                <Form.Control
                  type="date"
                  size="sm"
                  value={draft.to}
                  max={today}
                  onChange={(event) => setDraft({ ...draft, to: event.target.value })}
                />
              </label>
              <div className="window-selector__custom-foot">
                <span className="window-selector__hint">
                  {everything ? `The data starts on ${formatDay(everything.startDate, true)}.` : ''}
                </span>
                <Button type="submit" variant="primary" size="sm" disabled={!draft.from || !draft.to}>
                  Apply
                </Button>
              </div>
            </form>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <p className="window-selector__resolved" aria-live="polite">
        <span className="visually-hidden">The numbers cover </span>
        {days === null ? (
          <span className="window-selector__range">{value.label}</span>
        ) : (
          <>
            <span className="window-selector__range">{formatRange(value.startDate, shownEnd)}</span>
            <span className="window-selector__days">
              {' · '}
              {days.toLocaleString('en-NZ')} {days === 1 ? 'day' : 'days'}
              {shownEnd !== value.endDate && ' so far'}
            </span>
          </>
        )}
      </p>
    </div>
  );
}
